/**
 * Utility untuk mengelola device_id dari localStorage
 * Device_id tidak akan pernah dihapus dari localStorage
 * 
 * Format device_id:
 * - Web: base64("uuid|||user_agent")
 * - Mobile (Flutter): device_id dari Flutter (biasanya base64 encoded device info)
 */

const DEVICE_ID_KEY = "DEVICE_ID";
const SEPARATOR = "|||";

/**
 * Polyfill untuk UUID jika crypto.randomUUID() tidak tersedia (misal di test environment)
 */
const generateUUID = (): string => {
	// Check apakah crypto.randomUUID() tersedia
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}

	// Fallback untuk environment yang tidak support (test, old browser)
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
		const r = (Math.random() * 16) | 0;
		const v = c === "x" ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
};

/**
 * Get user agent dari browser
 */
const getUserAgent = (): string => {
	return navigator.userAgent;
};

/**
 * Generate device_id untuk web (uuid + user agent) lalu encode base64
 */
const generateWebDeviceId = (): string => {
	const uuid = generateUUID();
	const userAgent = getUserAgent();
	return btoa(`${uuid}${SEPARATOR}${userAgent}`);
};

/**
 * Get atau create device_id dari localStorage
 * Jika tidak ada, akan membuat yang baru (untuk web)
 * Return device_id yang siap dikirim ke backend
 */
export const getOrCreateDeviceId = (): string => {
	let deviceId = localStorage.getItem(DEVICE_ID_KEY);

	if (!deviceId) {
		// Generate device_id baru untuk web
		deviceId = generateWebDeviceId();
		localStorage.setItem(DEVICE_ID_KEY, deviceId);
		console.log("New web device_id created");
	}

	return deviceId;
};

/**
 * Set device_id ke localStorage (untuk mobile app)
 * Digunakan saat Flutter app mengirim device_id ke webview
 */
export const setDeviceId = (deviceId: string): void => {
	localStorage.setItem(DEVICE_ID_KEY, deviceId);
	console.log("Device_id set from mobile app");
};

/**
 * Parse device_id untuk mendapatkan informasi device
 * Bisa handle format web (uuid|||user_agent) atau mobile (base64)
 */
export const parseDeviceId = (deviceId: string): {
	type: "web" | "mobile";
	uuid?: string;
	userAgent?: string;
	mobileInfo?: string;
	mobileParts?: string[];
} => {
	// Coba decode base64 (baik web maupun mobile)
	try {
		const decoded = atob(deviceId);

		// Check apakah format web (mengandung separator)
		if (decoded.includes(SEPARATOR)) {
			const parts = decoded.split(SEPARATOR);
			return {
				type: "web",
				uuid: parts[0],
				userAgent: parts[1] || "Unknown",
			};
		}

		return {
			type: "mobile",
			mobileInfo: decoded,
			mobileParts: decoded.split("|"),
		};
	} catch (error) {
		// Jika gagal decode, kembalikan sebagai mobile info langsung
		return {
			type: "mobile",
			mobileInfo: deviceId,
		};
	}
};

/**
 * Get device_id yang sudah ada di localStorage
 * Return null jika belum ada
 */
export const getDeviceId = (): string | null => {
	return localStorage.getItem(DEVICE_ID_KEY);
};

/**
 * Get display name dari device_id untuk ditampilkan ke user
 */
export const getDeviceDisplayName = (deviceId: string): string => {
	const parsed = parseDeviceId(deviceId);

	if (parsed.type === "web") {
		return formatUserAgent(parsed.userAgent || "");
	}

	return formatMobileInfo(parsed.mobileParts, parsed.mobileInfo);
};

/**
 * Format user agent agar lebih readable untuk user
 */
const formatUserAgent = (userAgent: string): string => {
	if (!userAgent) return "Browser";

	const isWindows = /Windows/i.test(userAgent);
	const isMac = /Macintosh/i.test(userAgent);
	const isLinux = /Linux/i.test(userAgent);
	const isAndroid = /Android/i.test(userAgent);
	const isIOS = /iPhone|iPad/i.test(userAgent);

	let os = "Unknown OS";
	if (isWindows) os = "Windows";
	if (isMac) os = "macOS";
	if (isLinux) os = "Linux";
	if (isAndroid) os = "Android";
	if (isIOS) os = "iOS";

	let browser = "Browser";
	if (/Chrome/i.test(userAgent) && !/Edg/i.test(userAgent)) browser = "Chrome";
	if (/Edg/i.test(userAgent)) browser = "Edge";
	if (/Firefox/i.test(userAgent)) browser = "Firefox";
	if (/Safari/i.test(userAgent) && !/Chrome/i.test(userAgent)) browser = "Safari";

	return `${browser} • ${os}`;
};

/**
 * Format mobile info agar lebih readable untuk user
 * Format umum Flutter: build|model|brand|device|chipset
 */
const formatMobileInfo = (
	mobileParts?: string[],
	fallbackInfo?: string
): string => {
	if (mobileParts && mobileParts.length >= 3) {
		const model = mobileParts[1];
		const brand = mobileParts[2];
		return `${brand} ${model}`.trim();
	}

	return fallbackInfo || "Mobile Device";
};
